import { useEffect, useState } from 'react';
import { motion, type Variants } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { fetchCases } from '../lib/dataService';
import type { CaseItem } from '../types';

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.15 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

interface CasesProps {
  onContact: () => void;
}

export default function Cases({ onContact }: CasesProps) {
  const [cases, setCases] = useState<CaseItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetchCases().then((data) => {
      if (!cancelled) setCases(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section id="cases" className="bg-white py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.4 }}
          variants={container}
          className="mx-auto max-w-2xl text-center"
        >
          <motion.p
            variants={item}
            className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-600"
          >
            Cases
          </motion.p>
          <motion.h2
            variants={item}
            className="mt-3 text-3xl font-bold text-slate-900 sm:text-4xl lg:text-5xl"
          >
            服务案例
          </motion.h2>
          <motion.p variants={item} className="mt-4 text-slate-500">
            深耕行业场景，用真实项目见证我们的专业与实力
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          variants={container}
          className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-2"
        >
          {cases.map((caseItem) => (
            <motion.article
              key={caseItem.id}
              variants={item}
              className="lift-card group overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm"
            >
              {/* 案例图片（悬停缩放） */}
              <div className="relative aspect-[16/9] overflow-hidden bg-slate-100">
                <img
                  src={caseItem.image}
                  alt={caseItem.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                />
                <div className="absolute left-4 top-4 flex flex-wrap gap-2">
                  {(caseItem.tags ?? []).map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 px-3 py-1 text-xs font-semibold text-white shadow-md shadow-cyan-500/30"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="p-6 sm:p-7">
                <h3 className="text-lg font-bold text-slate-900 transition-colors duration-300 group-hover:text-cyan-600">
                  {caseItem.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  {caseItem.description}
                </p>
                <button
                  type="button"
                  onClick={onContact}
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-cyan-600 transition-all duration-300 hover:gap-3 hover:text-cyan-700"
                >
                  查看详情
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
